import PropTypes from 'prop-types';
import React, { Fragment, useEffect, useState } from 'react';
import { shallowEqual } from 'react-redux';
import Button from '../../../components/library/buttons/Button';
import TaskErrorText from '../../../components/library/workflow-tasks/TaskErrorText';
import useAutoAssignModal from '../../../hooks/useAutoAssignModalHook';
import { useMountDetection } from '../../../hooks/useMountDetection';
import '../../vdo-ticket/components/TicketBasicInformation.scss';
import TicketTab from '../../vdo-ticket/components/TicketTab';
import AutoAssignModalOfficesTab from './AutoAssignModalOfficesTab';
import AutoAssignModalProductsTab from './AutoAssignModalProductsTab';

const AutoAssignModalTabbedContainer = (props) => {

    const { impersonatingId, associate, onUpdated } = props

    const { loadOfficeLists, buildProductLists, saveAutoAssignability } = useAutoAssignModal(associate, impersonatingId)

    const mounted = useMountDetection()

    const errorType = Object.freeze({ generic: 1, ineligible: 2 })

    const [tab, setTab] = useState('offices');
    const [error, setError] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    // product objects for display in each list
    const allProducts = localStorage.getItem('autoassignable_products')?.split(",") || [];
    const [availableProducts, setAvailableProducts] = useState(null)
    const [assignedProducts, setAssignedProducts] = useState(null)

    // office objects for display in each list
    const [allOffices, setAllOffices] = useState(null)
    const [availableOffices, setAvailableOffices] = useState(null)
    const [assignedOffices, setAssignedOffices] = useState(null)
    const [homeOffice, setHomeOffice] = useState(null)

    const loadAutoAssignData = async () => {
        try {
            // set arrays to null to show loaders
            setAvailableOffices(null)
            setAssignedOffices(null)
            setAllOffices(null)
            setHomeOffice(null)
            setAvailableProducts(null)
            setAssignedProducts(null)

            // find available and assigned offices/products
            let offices = await loadOfficeLists()
            let products = await buildProductLists()

            if (mounted) {
                setHomeOffice(offices?.homeOffice || [])
                setAllOffices(offices?.allOffices || [])

                setAvailableOffices(offices?.availableOffices || [])
                setAssignedOffices(offices?.assignedOffices || [])

                setAvailableProducts(products?.availableProducts || [])
                setAssignedProducts(products?.assignedProducts || [])
            }
        } catch (ex) {
            console.log(ex)

            if (mounted) {
                setAllOffices([])
                setAvailableOffices([])
                setAssignedOffices([])
                setAvailableProducts([])
                setAssignedProducts([])
            }
        }
    }

    const onOfficesChanged = (assignedOfficeIds) => {
        // find assigned offices using the list of assigned office ids
        let newAssignedOffices = allOffices.filter(o => assignedOfficeIds.includes(o.id))
        if (!shallowEqual(assignedOffices, newAssignedOffices)) {
            setAssignedOffices(newAssignedOffices)
        }

        // available offices = all offices, minus home and assigned offices.
        let newAvailableOffices = allOffices.filter(o => !assignedOfficeIds.includes(o.id) && o.id !== associate.officeId)
        if (!shallowEqual(availableOffices, newAvailableOffices)) {
            setAvailableOffices(newAvailableOffices)
        }
    }

    const onProductsChanged = (assignedProductCodes) => {
        // find assigned products using the selected product codes
        let newAssignedProducts = allProducts.filter(o => assignedProductCodes.includes(o))
        if (!shallowEqual(assignedProducts, newAssignedProducts)) {
            setAssignedProducts(newAssignedProducts)
        }

        // what's left becomes available products
        let newAvailableProducts = allProducts.filter(o => !assignedProductCodes.includes(o))
        if (!shallowEqual(availableProducts, newAvailableProducts)) {
            setAvailableProducts(newAvailableProducts)
        }
    }

    const onSave = async () => {
        // perform the actual saving here
        try {
            setIsSaving(true)
            setError(null)

            var { officesResponse, productsResponse } =
                await saveAutoAssignability(assignedOffices.map(o => o.id), assignedProducts) || {}

            if (mounted) {
                // check for existing responses
                if (officesResponse && productsResponse) {
                    let { status, data: settings } = productsResponse
                    if (status === 200) {
                        // success! notify parent of update and close modal
                        onUpdated(status, settings)
                    } else if (status === 400) {
                        setError(errorType.ineligible)
                    } else {
                        setError(errorType.generic)
                        return;
                    }
                } else {
                    // something happened and we are missing a response or two
                    setError(errorType.generic)
                }
            }
        } catch (ex) {
            console.log(ex)
            if (mounted) {
                setError(errorType.generic)
            }
        } finally {
            if (mounted) {
                setIsSaving(false)
            }
        }
    }

    useEffect(() => {
        if (associate && !isSaving) {
            loadAutoAssignData()
        }
        return () => {
            // do some cleanup
            setTab('offices')
            setError(null)
            setHomeOffice(null)
            setAllOffices(null)
            setAvailableOffices(null)
            setAssignedOffices(null)
            setAvailableProducts(null)
            setAssignedProducts(null)
            
        }
    }, [(associate || {}).id])

     return (
        <Fragment>
            <div id='autoassign-tabs' className='tabs-outer auto-assign'>
                <div className='columns tickets'>
                    <div className='column is-11 is-offset-1'>
                        <div className='tab-holder'>
                             <TicketTab
                                 labelKey='AssociatesPage.AutoAssignTabs.Offices'
                                 selectedTab={tab}
                                 tabKey='offices'
                                 onClick={() => setTab('offices')} />
                             <TicketTab
                                 labelKey='AssociatesPage.AutoAssignTabs.Products'
                                 selectedTab={tab}
                                 tabKey='products'
                                 onClick={() => setTab('products')} />
                        </div>
                    </div>
                </div>
            </div>
             <div id={`autoassign-tab-${tab}`} className='light-functional-grey autoassign-tab-panel' >
                 {
                     {
                         'offices': <AutoAssignModalOfficesTab
                             {...props}
                             homeOffice={homeOffice}
                             availableOffices={availableOffices}
                             assignedOffices={assignedOffices}
                             onChanged={onOfficesChanged} />,
                         'products': <AutoAssignModalProductsTab
                             {...props}
                             availableProducts={availableProducts}
                             assignedProducts={assignedProducts}
                             onChanged={onProductsChanged} />
                     }[tab]
                 }
                 <div className='columns content my-1'>
                     <div className='column is-10 is-offset-1 columns px-0 is-vcentered'>
                         <div className='column is-9'>
                             {error && error === errorType.ineligible &&
                                 <TaskErrorText errorKey='AssociatesPage.AssignmentError' classNames='p2 error-text mt-0' />
                             }
                             {error && error !== errorType.ineligible &&
                                 <TaskErrorText errorKey='Common.GenericError' classNames='p2 error-text mt-0' />
                             }
                         </div>
                         <div className='column is-3'>
                             <div className='button-holder'>
                                 <Button
                                     elementId='autoassign-update-btn'
                                     labelKey='App.Update'
                                     loading={isSaving}
                                     buttonStyles='primary is-pulled-right'
                                     onClick={onSave} />
                             </div>
                         </div>
                     </div>
                 </div>
             </div>
        </Fragment>
    );
}

AutoAssignModalTabbedContainer.whyDidYouRender = false;

AutoAssignModalTabbedContainer.propTypes = {
    associate: PropTypes.object
}

export default AutoAssignModalTabbedContainer
